import { resolveMediaUrl } from "../lib/api";

// Detect and return an embed <iframe>/<video>/<img> for a single-line URL.
// Returns null if the URL is not a recognized embeddable.
function toEmbed(url) {
  const u = url.trim();
  if (!/^https?:\/\//i.test(u) && !u.startsWith("/")) return null;

  // YouTube
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
  if (yt) {
    return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}`, aspect: "video", label: "YouTube" };
  }

  // Vimeo
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) {
    return { kind: "iframe", src: `https://player.vimeo.com/video/${vm[1]}`, aspect: "video", label: "Vimeo" };
  }

  // Instagram post / reel
  const ig = u.match(/instagram\.com\/(?:p|reel|tv)\/([\w-]+)/);
  if (ig) {
    return { kind: "iframe", src: `https://www.instagram.com/p/${ig[1]}/embed`, aspect: "portrait", label: "Instagram" };
  }

  // TikTok video
  const tt = u.match(/tiktok\.com\/(?:@[^/]+\/video\/|v\/)(\d+)/);
  if (tt) {
    return { kind: "iframe", src: `https://www.tiktok.com/embed/v2/${tt[1]}`, aspect: "portrait", label: "TikTok" };
  }

  // Twitter / X — use Twitter's platform embed (no script required when using embed URL directly doesn't render; use syndication embed)
  const tw = u.match(/(?:twitter\.com|x\.com)\/[^/]+\/status\/(\d+)/);
  if (tw) {
    return { kind: "iframe", src: `https://platform.twitter.com/embed/Tweet.html?id=${tw[1]}&theme=dark`, aspect: "tweet", label: "Post" };
  }

  // Spotify — episode, show, playlist, track, album
  const sp = u.match(/open\.spotify\.com\/(episode|show|playlist|track|album)\/([A-Za-z0-9]+)/);
  if (sp) {
    const isAudio = sp[1] === "episode" || sp[1] === "show" || sp[1] === "track";
    return {
      kind: "iframe",
      src: `https://open.spotify.com/embed/${sp[1]}/${sp[2]}?theme=0`,
      aspect: isAudio ? "spotify-audio" : "spotify-playlist",
      label: "Spotify",
    };
  }

  // Apple Podcasts
  const ap = u.match(/podcasts\.apple\.com\/([a-z]{2})\/podcast\/[^/]+\/id(\d+)(?:.*?[?&]i=(\d+))?/);
  if (ap) {
    const region = ap[1];
    const showId = ap[2];
    const episodeId = ap[3];
    const embedPath = episodeId ? `podcast/id${showId}?i=${episodeId}` : `podcast/id${showId}`;
    return {
      kind: "iframe",
      src: `https://embed.podcasts.apple.com/${region}/${embedPath}`,
      aspect: "spotify-audio",
      label: "Apple Podcasts",
    };
  }

  // SoundCloud
  const sc = u.match(/soundcloud\.com\/[^/]+\/[^/?#]+/);
  if (sc) {
    return {
      kind: "iframe",
      src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(u)}&color=%23E36A53&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false`,
      aspect: "spotify-audio",
      label: "SoundCloud",
    };
  }

  // Direct image
  if (/\.(jpe?g|png|gif|webp|avif)(\?|$)/i.test(u)) {
    return { kind: "image", src: resolveMediaUrl(u), label: "Image" };
  }

  // Direct video file
  if (/\.(mp4|webm|mov|ogg)(\?|$)/i.test(u)) {
    return { kind: "video", src: resolveMediaUrl(u), label: "Video" };
  }

  return null;
}

function EmbedBlock({ url }) {
  const e = toEmbed(url);
  if (!e) return null;

  if (e.kind === "image") {
    return (
      <figure className="my-8 overflow-hidden border border-white/10 bg-black">
        <img src={e.src} alt="" className="h-auto w-full object-cover" />
      </figure>
    );
  }
  if (e.kind === "video") {
    return (
      <figure className="my-8 overflow-hidden border border-white/10 bg-black">
        <video src={e.src} controls className="h-auto w-full" />
      </figure>
    );
  }

  const aspectClass =
    e.aspect === "portrait"
      ? "aspect-[9/16] max-w-md mx-auto"
      : e.aspect === "tweet"
      ? "min-h-[500px]"
      : e.aspect === "spotify-audio"
      ? "h-[152px]"
      : e.aspect === "spotify-playlist"
      ? "h-[380px]"
      : "aspect-video";

  return (
    <div className={`my-8 w-full overflow-hidden border border-white/10 bg-black ${aspectClass}`}>
      <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4FF00]">{e.label}</div>
      <iframe
        src={e.src}
        title={e.label}
        className="h-full w-full"
        loading="lazy"
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        scrolling="no"
        frameBorder="0"
      />
    </div>
  );
}

// Render an article body where paragraphs that contain nothing but a single URL
// become inline embeds (YouTube, Instagram, TikTok, Twitter/X, Vimeo, images, videos).
export default function BodyRenderer({ body }) {
  if (!body) return null;
  const paragraphs = body.split(/\n\n+/);
  return (
    <div className="prose prose-invert max-w-none font-body text-base leading-relaxed text-white/80">
      {paragraphs.map((para, i) => {
        const trimmed = para.trim();
        // Use a content-based stable key (hashed from text + position) so that React
        // doesn't lose state when paragraphs reorder.
        const stableKey = `${i}:${trimmed.slice(0, 24)}`;
        // Single-line URL paragraph → embed
        if (/^https?:\/\/\S+$/.test(trimmed) || /^\/api\/files\/\S+$/.test(trimmed)) {
          const e = toEmbed(trimmed);
          if (e) return <EmbedBlock key={stableKey} url={trimmed} />;
        }
        return (
          <p key={stableKey} className="mb-6 whitespace-pre-wrap">
            {trimmed}
          </p>
        );
      })}
    </div>
  );
}

export { toEmbed };
